/**
 * packages/ui-shell/src/components/AdBanner.ts
 * Google AdSense 対応＆Pro版購入時自動非表示スマート下部広告バナー
 */
import { LicenseService } from '../services/LicenseService';
import { UiState } from '../state/UiState';
import { t, subscribeLocaleChange } from '../i18n/i18n';

export interface AdSenseConfig {
  clientId?: string; // 例: 'ca-pub-XXXXXXXXXXXXXXXX'
  slotId?: string;   // 例: '1234567890'
}

declare global {
  interface Window {
    __ADSENSE_CONFIG__?: AdSenseConfig;
    adsbygoogle?: unknown[];
  }
}

export interface AdBannerOptions {
  uiState: UiState;
  onRemoveAdsRequested: () => void;
  config?: AdSenseConfig;
}

export class AdBanner {
  private element: HTMLElement;
  private uiState: UiState;
  private onRemoveAdsRequested: () => void;
  private config?: AdSenseConfig;
  private unsubscribeLicense?: () => void;
  private unsubscribeLocale?: () => void;

  private houseAdEl?: HTMLElement;
  private adSenseContainerEl?: HTMLElement;
  private removeAdsBtnEl?: HTMLButtonElement;
  private sponsoredBadgeEl?: HTMLElement;
  private houseAdTextEl?: HTMLElement;

  constructor(options: AdBannerOptions) {
    this.uiState = options.uiState;
    this.onRemoveAdsRequested = options.onRemoveAdsRequested;
    this.config = options.config || (typeof window !== 'undefined' ? window.__ADSENSE_CONFIG__ : undefined);

    this.element = document.createElement('div');
    this.element.className = 'ad-banner-container';
    this.element.id = 'adBannerContainer';
    this.setupStyles();

    this.render();

    // ライセンス状態の購読 (Proが有効なら即座に完全非表示)
    const licenseService = LicenseService.getInstance();
    this.unsubscribeLicense = licenseService.subscribe((isPro) => {
      this.updateVisibility(isPro);
    });

    // 言語切り替え時のテキスト更新
    this.unsubscribeLocale = subscribeLocaleChange(() => {
      this.updateTexts();
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  private setupStyles(): void {
    const s = this.element.style;
    s.position = 'fixed';
    s.bottom = '0';
    s.left = '0';
    s.width = '100%';
    s.height = '48px';
    s.backgroundColor = '#ffffff';
    s.borderTop = '1px solid rgba(0, 0, 0, 0.08)';
    s.boxShadow = '0 -2px 10px rgba(0, 0, 0, 0.04)';
    s.display = 'flex';
    s.alignItems = 'center';
    s.justifyContent = 'space-between';
    s.padding = '0 16px';
    s.boxSizing = 'border-box';
    s.zIndex = '900'; // モーダル(1000)より下、キャンバスより上
    s.fontFamily = 'inherit';
    s.transition = 'transform 0.25s ease, opacity 0.25s ease';
  }

  private render(): void {
    const texts = t().ad;

    // Google AdSense の Client ID と Slot ID が設定されている場合
    const hasAdSense = !!(this.config?.clientId && this.config?.slotId);

    if (hasAdSense) {
      this.renderAdSenseUnit();
    } else {
      this.renderHouseAd();
    }
  }

  private renderHouseAd(): void {
    const texts = t().ad;
    const textsLegal = t().legal;

    this.element.innerHTML = `
      <div style="display: flex; align-items: center; gap: 10px; overflow: hidden; white-space: nowrap; cursor: pointer; flex: 1; min-width: 0;" id="adHouseAdArea">
        <span id="adSponsoredBadge" style="font-size: 10px; font-weight: 700; color: #888; background: #f0f0f0; padding: 2px 6px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.5px;">
          ${texts.sponsored}
        </span>
        <span id="adHouseAdText" style="font-size: 12px; font-weight: 600; color: #333; overflow: hidden; text-overflow: ellipsis;">
          ${texts.houseAdText}
        </span>
      </div>
      <div style="display: flex; align-items: center; gap: 10px; flex-shrink: 0; margin-left: 12px;">
        <div id="adLegalLinks" style="font-size: 11px; color: #888; display: flex; align-items: center; gap: 6px;">
          <a href="/privacy.html" id="adLinkPrivacy" style="color: #666; text-decoration: underline; cursor: pointer;">${textsLegal.footerPrivacy}</a>
          <span style="opacity: 0.5;">•</span>
          <a href="/terms.html" id="adLinkTerms" style="color: #666; text-decoration: underline; cursor: pointer;">${textsLegal.footerTerms}</a>
          <span style="opacity: 0.5;">•</span>
          <a href="https://x.com/KILROYSANWHERE" target="_blank" rel="noopener noreferrer" style="color: #666; text-decoration: underline; font-weight: 600;">𝕏 @KILROYSANWHERE</a>
        </div>
        <button type="button" id="btnAdRemove" class="btn btn-secondary" style="font-size: 11px; font-weight: 700; padding: 5px 12px; border-radius: 20px; color: #b45309; background: #fef3c7; border: 1px solid #fde68a; cursor: pointer; white-space: nowrap; transition: all 0.2s;">
          ${texts.removeAdsBtn}
        </button>
      </div>
    `;

    this.sponsoredBadgeEl = this.element.querySelector('#adSponsoredBadge') as HTMLElement;
    this.houseAdTextEl = this.element.querySelector('#adHouseAdText') as HTMLElement;
    this.removeAdsBtnEl = this.element.querySelector('#btnAdRemove') as HTMLButtonElement;

    // クリックでアップグレードモーダルを表示
    this.element.querySelector('#adHouseAdArea')?.addEventListener('click', () => {
      this.onRemoveAdsRequested();
    });

    this.removeAdsBtnEl?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onRemoveAdsRequested();
    });
  }

  private renderAdSenseUnit(): void {
    const texts = t().ad;
    const clientId = this.config!.clientId;
    const slotId = this.config!.slotId;

    this.element.innerHTML = `
      <div style="flex: 1; display: flex; align-items: center; justify-content: center; height: 100%; overflow: hidden;" id="adSenseContainer">
        <ins class="adsbygoogle"
             style="display:inline-block;width:100%;height:48px;"
             data-ad-client="${clientId}"
             data-ad-slot="${slotId}"
             data-ad-format="horizontal"
             data-full-width-responsive="true"></ins>
      </div>
      <div style="margin-left: 8px; flex-shrink: 0;">
        <button type="button" id="btnAdSenseRemove" class="btn btn-secondary" style="font-size: 10px; font-weight: 700; padding: 4px 8px; border-radius: 12px; color: #888; background: #f5f5f5; border: 1px solid #ddd; cursor: pointer; white-space: nowrap;">
          ${texts.removeAdsBtn}
        </button>
      </div>
    `;

    this.element.querySelector('#btnAdSenseRemove')?.addEventListener('click', () => {
      this.onRemoveAdsRequested();
    });

    // Google AdSense スクリプトを実行
    try {
      if (typeof window !== 'undefined') {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (err) {
      console.warn('[AdBanner] AdSense push error:', err);
    }
  }

  private updateVisibility(isPro: boolean): void {
    if (isPro) {
      this.element.style.display = 'none';
    } else {
      this.element.style.display = 'flex';
    }
  }

  private updateTexts(): void {
    const texts = t().ad;
    const textsLegal = t().legal;
    if (this.sponsoredBadgeEl) {
      this.sponsoredBadgeEl.textContent = texts.sponsored;
    }
    if (this.houseAdTextEl) {
      this.houseAdTextEl.textContent = texts.houseAdText;
    }
    if (this.removeAdsBtnEl) {
      this.removeAdsBtnEl.textContent = texts.removeAdsBtn;
    }
    const linkPriv = this.element.querySelector('#adLinkPrivacy');
    if (linkPriv) linkPriv.textContent = textsLegal.footerPrivacy;
    const linkTerms = this.element.querySelector('#adLinkTerms');
    if (linkTerms) linkTerms.textContent = textsLegal.footerTerms;
  }

  destroy(): void {
    this.unsubscribeLicense?.();
    this.unsubscribeLocale?.();
    this.element.remove();
  }
}
